export type ShareMethod = 'web-share' | 'web-share-url-only' | 'clipboard' | 'new-tab' | 'rejected';

export interface ShareResult {
  success: boolean;
  error?: string;
  method?: ShareMethod;
}

class ShareService {
  /**
   * Share a URL using Web Share API or fallback to copying to clipboard
   */
  async shareUrl(url: string, text?: string, title?: string): Promise<ShareResult> {
    // Reject base64 images - indicates backend failed to save to S3
    if (url.startsWith('data:')) {
      return {
        success: false,
        error: 'Unable to generate share image. Please try again later.',
        method: 'rejected',
      };
    }

    if (navigator.share) {
      await navigator.share({
        title: title || 'Recap 2025',
        text: text || '',
        url: url,
      });
      return { success: true, method: 'web-share' };
    } else {
      console.log('Web Share API not supported', {
        hasShare: 'share' in navigator,
        hasCanShare: 'canShare' in navigator,
        isSecureContext: window.isSecureContext,
        inIframe: window.top !== window.self,
      });
    }

    try {
      // Check if Web Share API is available AND can share this URL
      // canShare() can throw if blocked by permissions policy, so wrap in try-catch
      let canShare = false;
      try {
        canShare = 'share' in navigator && 'canShare' in navigator && (navigator as any).canShare({ url });
      } catch {
        // canShare threw - permissions policy blocked, fall through to fallbacks
      }

      if (canShare) {
        await (navigator as any).share({ url });
        return { success: true, method: 'web-share-url-only' };
      }

      // Fallback: Copy to clipboard
      if ('clipboard' in navigator && (navigator as any).clipboard) {
        try {
          await (navigator as any).clipboard.writeText(url);
          return { success: true, error: 'Link copied to clipboard', method: 'clipboard' };
        } catch {
          // Clipboard blocked by permissions policy, fall through to window.open
        }
      }

      // Final fallback: Open URL in new tab
      window.open(url, '_blank');
      return { success: true, error: 'Opening share link in new tab', method: 'new-tab' };
    } catch (error) {
      if ((error as Error).name === 'AbortError') {
        // User cancelled the share dialog - not an error, just silently return success
        return { success: true, method: 'web-share' };
      }

      console.error('Failed to share URL:', error);

      // Last resort: try opening in new tab
      try {
        window.open(url, '_blank');
        return { success: true, error: 'Opening share link in new tab', method: 'new-tab' };
      } catch {
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Failed to share link',
          method: 'rejected',
        };
      }
    }
  }

  /**
   * Check if Web Share API is available
   */
  isShareSupported(): boolean {
    return 'share' in navigator && 'canShare' in navigator;
  }
}

export const shareService = new ShareService();
